import {
  Fragment,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn';
import {
  MAX_FIT_PX,
  MIN_COLUMN_PX,
  applyOrder,
  isCustomLayout,
  useTableLayout,
} from '../lib/use-table-layout';
import { GROUP_LABEL } from './group-label';
import { Menu, MenuContent, MenuItem, MenuTrigger } from './menu';
import { Tooltip } from './tooltip';

export interface IColumn<TRow> {
  key: string;
  header: string;
  width: string | 'grow';
  grow?: boolean;
  align?: 'right';
  ui?: boolean;
  hint?: ReactNode;
  /** Not dragged, not resized, kept in place (a checkbox, a row menu); headerless columns are. */
  pinned?: boolean;
  render: (row: TRow) => ReactNode;
}

const HEIGHT = {
  row: 'h-row',
  lg: 'h-row-lg',
  xl: 'h-row-xl',
  tall: 'h-row-2line',
} as const;

export type TRowHeight = keyof typeof HEIGHT;

export interface ISubRow<TColumn> {
  key: string;
  cell: (column: TColumn) => ReactNode;
  onOpen?: () => void;
  className?: string;
  height?: TRowHeight;
}

interface ITableProps<TRow, TColumn extends IColumn<TRow>> {
  columns: readonly TColumn[];
  rows: readonly TRow[];
  rowKey: (row: TRow) => string;
  muted?: (row: TRow) => boolean;
  onRowClick?: (row: TRow) => void;
  selectedKey?: string | null;
  /** Room for a second line under the first, e.g. a name with an id beneath it. */
  tall?: boolean;
  height?: TRowHeight;
  /** Identity for the reader's own column order and widths. No id, no dragging. */
  layoutId?: string;
  /** Rows of another shape above the data rows, under the header (folders above items). */
  lead?: ReactNode;
  /** Wraps each row, e.g. to make it draggable; the row itself stays as drawn. */
  wrapRow?: (row: TRow, drawn: ReactNode) => ReactNode;
  rowClassName?: (row: TRow) => string | false | undefined;
  onRowKeyDown?: (row: TRow, event: KeyboardEvent<HTMLDivElement>) => void;
  gutter?: (row: TRow) => ReactNode;
  /** What opens under a row, drawn with `drawRow` so its cells follow the reader's columns. */
  expanded?: (row: TRow, drawRow: (sub: ISubRow<TColumn>) => ReactNode) => ReactNode;
}

const fixed = (column: Pick<IColumn<never>, 'pinned' | 'header'>) =>
  column.pinned ?? column.header === '';

// ↑ / ↓ walk every focusable row in the order drawn: data rows, lead rows, opened sub-rows.
const walkRows = (event: KeyboardEvent<HTMLElement>) => {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
  const table = event.currentTarget.closest('[role="table"]');
  if (!table) return;
  const rows = [...table.querySelectorAll<HTMLElement>('[role="row"][tabindex]')];
  const next = rows[rows.indexOf(event.currentTarget) + (event.key === 'ArrowDown' ? 1 : -1)];
  if (!next) return;
  event.preventDefault();
  next.focus();
  next.scrollIntoView({ block: 'nearest' });
};

// The width a cell's content wants, unclipped: a hidden copy laid out at max-content.
const naturalWidth = (cell: HTMLElement): number => {
  const probe = cell.cloneNode(true) as HTMLElement;
  probe.style.cssText =
    'position:absolute;visibility:hidden;width:max-content;min-width:0;max-width:none;flex:none';
  // A `flex-1` child has a zero basis and adds nothing to max-content; unshrunk, it counts.
  for (const inner of probe.querySelectorAll<HTMLElement>('*')) {
    inner.style.flexShrink = '0';
    inner.style.flexBasis = 'auto';
    inner.style.maxWidth = 'none';
  }
  cell.parentElement?.appendChild(probe);
  const width = probe.getBoundingClientRect().width;
  probe.remove();
  return width;
};

export const Table = <TRow, TColumn extends IColumn<TRow> = IColumn<TRow>>({
  columns,
  rows,
  rowKey,
  muted,
  onRowClick,
  selectedKey,
  tall,
  height,
  layoutId,
  lead,
  wrapRow,
  rowClassName,
  onRowKeyDown,
  gutter,
  expanded,
}: ITableProps<TRow, TColumn>) => {
  const { layout, setWidth, setOrder, reset } = useTableLayout(layoutId);
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [menuAt, setMenuAt] = useState<number | null>(null);
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);

  const editable = Boolean(layoutId);
  const rowHeight = HEIGHT[height ?? (tall ? 'tall' : 'row')];

  // Pinned columns hold their declared places; the reader's order fills the rest.
  const movable = applyOrder(
    columns.filter((column) => !fixed(column)),
    layout.order,
  );
  const ordered: TColumn[] = [];
  let nextMovable = 0;
  for (const column of columns) {
    ordered.push(fixed(column) ? column : movable[nextMovable++]!);
  }

  const widthOf = (column: TColumn): CSSProperties | undefined => {
    const px = layout.widths[column.key];
    return px === undefined ? undefined : { width: px, flexGrow: 0, flexShrink: 0 };
  };

  const widthClass = (column: TColumn) => {
    if (layout.widths[column.key] !== undefined) return 'shrink-0';
    if (column.width === 'grow') return 'min-w-0 flex-1';
    return column.grow ? `flex-1 ${column.width}` : `shrink-0 ${column.width}`;
  };

  // Never narrower than its fixed columns plus a floor for each growing one: the page then
  // scrolls sideways instead of a growing column being squeezed to nothing.
  useLayoutEffect(() => {
    const frame = tableRef.current?.parentElement;
    const measure = () => {
      const cells = [...(headerRef.current?.children ?? [])] as HTMLElement[];
      const total = cells.reduce((sum, cell) => {
        const style = getComputedStyle(cell);
        if (style.flexGrow === '0') return sum + cell.getBoundingClientRect().width;
        const floor = Number.parseFloat(style.minWidth) || 0;
        return sum + Math.max(MIN_COLUMN_PX * 2, floor);
      }, 0);
      const next = Math.ceil(total);
      setMinWidth((current) => (current === next ? current : next));
    };
    measure();
    if (!frame) return;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  });

  const movableBoxes = () =>
    [...(headerRef.current?.querySelectorAll('[data-movable]') ?? [])] as HTMLElement[];

  const startResize = (event: PointerEvent<HTMLSpanElement>, column: TColumn) => {
    event.preventDefault();
    event.stopPropagation();

    const cell = event.currentTarget.closest('[data-column]') as HTMLElement | null;
    if (!cell) return;

    const startX = event.clientX;
    const startWidth = cell.getBoundingClientRect().width;
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);

    const move = (moved: globalThis.PointerEvent) =>
      setWidth(column.key, Math.max(MIN_COLUMN_PX, startWidth + moved.clientX - startX));

    const done = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', done);
      handle.removeEventListener('pointercancel', done);
    };

    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', done);
    handle.addEventListener('pointercancel', done);
  };

  const autoFit = (column: TColumn) => {
    const cells =
      tableRef.current?.querySelectorAll<HTMLElement>(
        `[data-column="${CSS.escape(column.key)}"]`,
      ) ?? [];
    let widest = MIN_COLUMN_PX;
    for (const cell of cells) widest = Math.max(widest, naturalWidth(cell));
    setWidth(column.key, Math.min(MAX_FIT_PX, Math.ceil(widest) + 1));
  };

  const startReorder = (event: PointerEvent<HTMLSpanElement>, column: TColumn) => {
    if (!editable || fixed(column) || event.button !== 0) return;

    const origin = event.clientX;
    const target = event.currentTarget;
    // Refs, not the state below: that state only draws the drag, and reading it
    // back inside a setState updater would run this commit twice in StrictMode.
    let armed = false;
    let landing: number | null = null;
    // Captured from the press: a quick flick leaves the header before the slack is used up.
    target.setPointerCapture(event.pointerId);

    const move = (moved: globalThis.PointerEvent) => {
      // Slack, or a click on a header becomes a drag and its tooltip never opens.
      if (!armed && Math.abs(moved.clientX - origin) < 4) return;
      if (!armed) {
        armed = true;
        setDragging(column.key);
      }

      const boxes = movableBoxes();
      const over = boxes.findIndex((box) => {
        const rect = box.getBoundingClientRect();
        return moved.clientX < rect.left + rect.width / 2;
      });
      landing = over === -1 ? boxes.length : over;
      setDropIndex(landing);
    };

    const done = () => {
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', done);
      target.removeEventListener('pointercancel', done);
      setDragging(null);
      setDropIndex(null);

      if (!armed || landing === null) return;

      const from = movable.findIndex((item) => item.key === column.key);
      const keys = movable.map((item) => item.key).filter((key) => key !== column.key);
      keys.splice(landing > from ? landing - 1 : landing, 0, column.key);
      setOrder(keys);
    };

    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', done);
    target.addEventListener('pointercancel', done);
  };

  const cellClass = (column: TColumn, faint: boolean) =>
    cn(
      'truncate px-inset text-data',
      widthClass(column),
      column.align === 'right' && 'text-right',
      column.ui ? 'font-sans' : 'font-mono',
      faint && 'text-faint',
    );

  const gutterCell = (content: ReactNode) =>
    gutter && (
      <span data-gutter className="flex w-6 shrink-0 justify-center">
        {content}
      </span>
    );

  const drawSub = (row: TRow) => (sub: ISubRow<TColumn>) => (
    <div
      key={sub.key}
      role="row"
      tabIndex={sub.onOpen ? 0 : undefined}
      onClick={sub.onOpen}
      onKeyDown={(event) => {
        if (sub.onOpen && event.key === 'Enter') sub.onOpen();
        walkRows(event);
      }}
      className={cn(
        'group flex items-center border-b border-border-row',
        HEIGHT[sub.height ?? 'row'],
        sub.onOpen && 'cursor-pointer hover:bg-hover focus-visible:bg-hover',
        sub.className,
      )}
    >
      {gutterCell(null)}
      {ordered.map((column) => (
        <span
          key={column.key}
          role="cell"
          data-column={column.key}
          style={widthOf(column)}
          className={cellClass(column, Boolean(muted?.(row)))}
        >
          {sub.cell(column)}
        </span>
      ))}
    </div>
  );

  let movableIndex = -1;

  return (
    // The frame scrolls sideways once the reader's widths outgrow the page; header and rows move together.
    <div className="overflow-x-auto">
      <div ref={tableRef} role="table" style={minWidth ? { minWidth } : undefined}>
        <Menu open={menuAt !== null} onOpenChange={(open) => !open && setMenuAt(null)}>
          <div
            ref={headerRef}
            role="row"
            onContextMenu={
              editable
                ? (event) => {
                    event.preventDefault();
                    setMenuAt(event.clientX - event.currentTarget.getBoundingClientRect().left);
                  }
                : undefined
            }
            className="relative flex h-control-xs items-center border-b border-border-sunken"
          >
            {gutterCell(null)}
            {ordered.map((column) => {
              const canMove = editable && !fixed(column);
              if (canMove) movableIndex += 1;
              return (
                <span
                  key={column.key}
                  data-column={column.key}
                  data-movable={canMove || undefined}
                  role="columnheader"
                  style={widthOf(column)}
                  onPointerDown={(event) => startReorder(event, column)}
                  className={cn(
                    'relative flex items-center truncate px-inset',
                    GROUP_LABEL,
                    widthClass(column),
                    column.align === 'right' && 'justify-end text-right',
                    canMove && 'cursor-grab select-none',
                    dragging === column.key && 'opacity-40',
                    canMove &&
                      dropIndex === movableIndex &&
                      dragging &&
                      'border-l border-border-focus',
                  )}
                >
                  {column.hint ? (
                    <Tooltip content={column.hint}>
                      <span className="cursor-help truncate">{column.header}</span>
                    </Tooltip>
                  ) : (
                    <span className="truncate" title={column.header || undefined}>
                      {column.header}
                    </span>
                  )}

                  {canMove && (
                    <span
                      aria-hidden
                      title="Drag to resize, double-click to fit the content"
                      onPointerDown={(event) => startResize(event, column)}
                      onDoubleClick={() => autoFit(column)}
                      className="absolute inset-y-0 -right-px w-inset cursor-col-resize hover:bg-border-focus"
                    />
                  )}
                </span>
              );
            })}
            {editable && (
              <MenuTrigger asChild>
                <span
                  aria-hidden
                  style={{ left: menuAt ?? 0 }}
                  className="pointer-events-none absolute bottom-0"
                />
              </MenuTrigger>
            )}
          </div>
          {editable && (
            <MenuContent align="start" alignOffset={0}>
              <MenuItem disabled={!isCustomLayout(layout)} onSelect={reset}>
                Reset columns
              </MenuItem>
            </MenuContent>
          )}
        </Menu>

        {lead}

        {rows.map((row) => {
          const key = rowKey(row);
          const faint = Boolean(muted?.(row));
          const drawn = (
            <div
              key={key}
              role="row"
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={(event) => {
                if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onRowClick(row);
                }
                onRowKeyDown?.(row, event);
                walkRows(event);
              }}
              className={cn(
                'group flex items-center border-b border-border-row',
                rowHeight,
                onRowClick && 'cursor-pointer hover:bg-hover focus-visible:bg-hover',
                key === selectedKey && 'bg-highlight',
                faint && 'text-faint',
                rowClassName?.(row),
              )}
            >
              {gutterCell(gutter?.(row))}
              {ordered.map((column) => (
                <span
                  key={column.key}
                  role="cell"
                  data-column={column.key}
                  style={widthOf(column)}
                  className={cellClass(column, faint)}
                >
                  {column.render(row)}
                </span>
              ))}
            </div>
          );
          const wrapped = wrapRow ? wrapRow(row, drawn) : drawn;
          // One shape whether open or not: a row that re-parents on toggle loses the keyboard focus.
          return expanded ? (
            <div key={key} role="rowgroup">
              {wrapped}
              {expanded(row, drawSub(row))}
            </div>
          ) : (
            <Fragment key={key}>{wrapped}</Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const TruncateMiddle = ({ text, tail = 10 }: { text: string; tail?: number }) => {
  if (text.length <= tail + 4) return <span className="truncate">{text}</span>;

  const window = text.slice(-(tail + 6));
  const space = window.indexOf(' ');
  const keep = space >= 0 && space < 6 ? tail + 6 - space : tail;

  // `whitespace-pre`: a flex item trims its own edge whitespace, and the split usually falls on a space.
  return (
    <span className="flex min-w-0 whitespace-pre" title={text}>
      <span className="truncate">{text.slice(0, text.length - keep)}</span>
      <span className="shrink-0">{text.slice(-keep)}</span>
    </span>
  );
};

export const ShortId = ({ id, keep = 11 }: { id: string; keep?: number }) => {
  const cut = id.indexOf('_') + 1 + keep;
  if (id.length <= cut) return <span>{id}</span>;

  return (
    <span title={id}>
      {id.slice(0, cut)}
      <span className="text-dim">…</span>
    </span>
  );
};
